import { useFrame, useThree } from '@react-three/fiber';
import { type Ref, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';

/**
 * Civilization-style camera: faces north, never rotates.
 *
 * - Pan: right/middle-drag the ground (the grabbed point stays under the
 *   cursor), or WASD / arrow keys. Panning keeps the camera height.
 * - Zoom: mouse wheel, smoothed, towards the cursor: the ground point under
 *   the cursor stays under it. The camera tilts flatter as it zooms in so
 *   close-ups see models more from the side; the tilt depends only on zoom.
 * - Pairs with a narrow field of view (set on the Canvas) to keep
 *   perspective distortion low; distances below are tuned for ~32°.
 * - The look-at point is kept within `boundsRadius` of the map centre.
 *
 * Left-drag is box-select's (`useBoxSelect`). Drags only start after
 * `dragThreshold` px, so plain clicks (select, orders) are left alone.
 */

export interface StrategyCameraHandle {
  /** Centre the view on a world point, keeping the current zoom. */
  panTo: (x: number, z: number) => void;
  /** Where it looks and how far out it is (to restore after the Canvas remounts). */
  getView: () => CameraView;
  setView: (view: CameraView) => void;
}

export interface CameraView {
  x: number;
  z: number;
  distance: number;
}

interface StrategyCameraProps {
  ref?: Ref<StrategyCameraHandle>;
  /** Height of the ground plane that dragging grabs. */
  groundY: number;
  boundsRadius: number;
  dragThreshold: number;
  /** Keys (lower-case) the match claims right now, so they don't pan (A while fighters are selected). */
  isKeyClaimed?: (key: string) => boolean;
}

/** Angle between the view direction and the ground, fully zoomed in / out. */
const PITCH_NEAR = THREE.MathUtils.degToRad(42);
const PITCH_FAR = THREE.MathUtils.degToRad(58);
const MIN_DISTANCE = 11;
const MAX_DISTANCE = 100;
const START_DISTANCE = 34;
const WHEEL_ZOOM_SPEED = 0.0015;
const ZOOM_SMOOTHING = 12;
/** Keyboard pan speed, in multiples of the current camera distance per second. */
const KEY_PAN_SPEED = 0.7;

const PAN_KEYS: Record<string, [number, number]> = {
  w: [0, -1],
  arrowup: [0, -1],
  s: [0, 1],
  arrowdown: [0, 1],
  a: [-1, 0],
  arrowleft: [-1, 0],
  d: [1, 0],
  arrowright: [1, 0],
};

/** Pitch for a camera distance, interpolated on the log scale the wheel zooms on. */
function pitchAt(distance: number): number {
  const t = Math.log(distance / MIN_DISTANCE) / Math.log(MAX_DISTANCE / MIN_DISTANCE);
  return THREE.MathUtils.lerp(PITCH_NEAR, PITCH_FAR, THREE.MathUtils.clamp(t, 0, 1));
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

export function StrategyCamera({
  ref,
  groundY,
  boundsRadius,
  dragThreshold,
  isKeyClaimed,
}: StrategyCameraProps) {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);

  const target = useRef(new THREE.Vector3(0, groundY, 0));
  const distance = useRef(START_DISTANCE);
  const goalDistance = useRef(START_DISTANCE);
  const keysDown = useRef(new Set<string>());
  /** Cursor position of the last wheel zoom, whose ground point zooming keeps in place. */
  const zoomAnchor = useRef<{ x: number; y: number } | null>(null);

  // Latest helpers behind a ref, so the event effect subscribes once.
  const helpers = useRef({
    clamp: () => {},
    place: () => {},
    groundAt: (_x: number, _y: number): THREE.Vector3 | null => null,
  });
  const plane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), -groundY));
  plane.current.constant = -groundY;
  const raycaster = useRef(new THREE.Raycaster());
  helpers.current = {
    /** Ground point under a screen position, or null when it misses the plane. */
    groundAt(clientX, clientY) {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.current.setFromCamera(ndc, camera);
      const hit = new THREE.Vector3();
      return raycaster.current.ray.intersectPlane(plane.current, hit) ? hit : null;
    },
    clamp() {
      const t = target.current;
      const r = Math.hypot(t.x, t.z);
      if (r > boundsRadius) {
        t.x *= boundsRadius / r;
        t.z *= boundsRadius / r;
      }
    },
    place() {
      const d = distance.current;
      const t = target.current;
      const pitch = pitchAt(d);
      camera.position.set(t.x, t.y + d * Math.sin(pitch), t.z + d * Math.cos(pitch));
      camera.lookAt(t);
      camera.updateMatrixWorld();
    },
  };

  useImperativeHandle(
    ref,
    () => ({
      panTo(x, z) {
        zoomAnchor.current = null;
        target.current.set(x, groundY, z);
        helpers.current.clamp();
        helpers.current.place();
      },
      getView: () => ({ x: target.current.x, z: target.current.z, distance: goalDistance.current }),
      setView({ x, z, distance: d }) {
        zoomAnchor.current = null;
        target.current.set(x, groundY, z);
        distance.current = d;
        goalDistance.current = d;
        helpers.current.clamp();
        helpers.current.place();
      },
    }),
    [groundY]
  );

  // Pointer drag-to-pan, wheel zoom, keyboard pan.
  useEffect(() => {
    // The canvas's container: drei `Html` overlays (clickable fog city
    // markers) are portalled into it, so wheel and drag over them still reach us.
    const el = gl.domElement.parentElement ?? gl.domElement;
    const groundAt = (x: number, y: number) => helpers.current.groundAt(x, y);

    let press: { x: number; y: number } | null = null;
    let grab: THREE.Vector3 | null = null;

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 1 && e.button !== 2) return;
      press = { x: e.clientX, y: e.clientY };
      grab = null;
    }
    function onPointerMove(e: PointerEvent) {
      if (!press) return;
      if (!grab) {
        if (Math.hypot(e.clientX - press.x, e.clientY - press.y) <= dragThreshold) return;
        grab = groundAt(press.x, press.y);
        if (!grab) return;
      }
      const now = groundAt(e.clientX, e.clientY);
      if (!now) return;
      zoomAnchor.current = null;
      target.current.x += grab.x - now.x;
      target.current.z += grab.z - now.z;
      helpers.current.clamp();
      helpers.current.place();
    }
    function onPointerUp() {
      press = null;
      grab = null;
    }
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      goalDistance.current = THREE.MathUtils.clamp(
        goalDistance.current * Math.exp(e.deltaY * WHEEL_ZOOM_SPEED),
        MIN_DISTANCE,
        MAX_DISTANCE
      );
      zoomAnchor.current = { x: e.clientX, y: e.clientY };
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      const key = e.key.toLowerCase();
      if (!(key in PAN_KEYS) || isKeyClaimed?.(key)) return;
      e.preventDefault();
      keysDown.current.add(key);
    }
    function onKeyUp(e: KeyboardEvent) {
      keysDown.current.delete(e.key.toLowerCase());
    }
    function onBlur() {
      keysDown.current.clear();
      press = null;
      grab = null;
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [gl, dragThreshold, isKeyClaimed]);

  useFrame((_, dt) => {
    const step = Math.min(dt, 0.1);
    let moved = false;

    if (keysDown.current.size > 0) {
      let dx = 0;
      let dz = 0;
      for (const key of keysDown.current) {
        // Claimed since it went down (fighters got selected): stop, don't stick.
        if (isKeyClaimed?.(key)) {
          keysDown.current.delete(key);
          continue;
        }
        const dir = PAN_KEYS[key];
        if (dir) {
          dx += dir[0];
          dz += dir[1];
        }
      }
      const len = Math.hypot(dx, dz);
      if (len > 0) {
        const speed = KEY_PAN_SPEED * distance.current * step;
        target.current.x += (dx / len) * speed;
        target.current.z += (dz / len) * speed;
        helpers.current.clamp();
        moved = true;
      }
    }

    const gap = goalDistance.current - distance.current;
    if (Math.abs(gap) > 1e-3) {
      // Keep the ground point under the cursor in place while zooming.
      const anchor = zoomAnchor.current;
      if (moved) helpers.current.place();
      const before = anchor && helpers.current.groundAt(anchor.x, anchor.y);
      distance.current += gap * (1 - Math.exp(-ZOOM_SMOOTHING * step));
      helpers.current.place();
      const after = anchor && helpers.current.groundAt(anchor.x, anchor.y);
      if (before && after) {
        target.current.x += before.x - after.x;
        target.current.z += before.z - after.z;
        helpers.current.clamp();
      }
      moved = true;
    } else {
      zoomAnchor.current = null;
    }

    if (moved) helpers.current.place();
  });

  // Initial placement.
  useEffect(() => {
    helpers.current.place();
  }, []);

  return null;
}
